import type { ReactElement } from 'react';
import { useTranslation } from 'next-i18next';
import type { UseAlgoliaFilterReturn } from '../../../../hooks/useAlgoliaFilter';
import { FacetNames } from '../../../../types/algolia';
import ColorFilter from '../ColorFilter';
import { FilterCheckboxAccordion } from '../FilterCheckboxAccordion';
import { HeelHeightFilter } from '../HeelHeightFilter';
import { PriceFilter } from '../PriceFilter';
import RatingsFilter from '../RatingsFilter';
import SizeFilter from '../sizeFilter/SizeFilter';

export function RefinementFacet({
  algoliaFilter,
  facet,
  productCount,
}: {
  algoliaFilter: UseAlgoliaFilterReturn;
  facet: string;
  productCount: number;
}): ReactElement | null {
  const { t } = useTranslation(['lib-global-common']);

  const {
    sizeRefinements,
    priceRange,
    colorRefinements,
    styleRefinements,
    fasteningRefinements,
    materialsRefinements,
    ratingRefinements,
    widthRefinements,
    technologiesRefinements,
    productTypeRefinements,
    heelHeightRefinements,
    genderRefinements,
    brandRefinements,
  } = algoliaFilter;

  switch (facet) {
    case FacetNames.SIZE:
      return <SizeFilter {...sizeRefinements} productCount={productCount} />;

    case FacetNames.FIT:
      return (
        <FilterCheckboxAccordion
          productCount={productCount}
          accordionLabel={t('filter.menu.filter.width')}
          {...widthRefinements}
        />
      );

    case FacetNames.HEEL_HEIGHT:
      return (
        <HeelHeightFilter
          productCount={productCount}
          accordionLabel={t('filter.menu.filter.heel.height')}
          {...heelHeightRefinements}
        />
      );

    case FacetNames.COLOR:
      return <ColorFilter accordionLabel={t('filter.menu.filter.colour')} {...colorRefinements} />;

    case FacetNames.GENDER:
      return (
        <FilterCheckboxAccordion
          productCount={productCount}
          accordionLabel={t('filter.menu.filter.gender')}
          {...genderRefinements}
        />
      );

    case FacetNames.PRODUCT:
      return (
        <FilterCheckboxAccordion
          productCount={productCount}
          accordionLabel={t('filter.menu.filter.product.type')}
          {...productTypeRefinements}
        />
      );
    case FacetNames.STYLE:
      return (
        <FilterCheckboxAccordion
          productCount={productCount}
          accordionLabel={t('filter.menu.filter.style')}
          {...styleRefinements}
        />
      );

    case FacetNames.FASTENING:
      return (
        <FilterCheckboxAccordion
          productCount={productCount}
          accordionLabel={t('filter.menu.filter.fastening')}
          {...fasteningRefinements}
        />
      );

    case FacetNames.TECHNOLOGY:
      return (
        <FilterCheckboxAccordion
          productCount={productCount}
          accordionLabel={t('filter.menu.filter.technology')}
          {...technologiesRefinements}
        />
      );

    case FacetNames.TOP_RATED:
      return (
        <RatingsFilter
          productCount={productCount}
          accordionLabel={t('filter.menu.filter.top.rated')}
          {...ratingRefinements}
        />
      );

    case FacetNames.BRAND:
      return (
        <FilterCheckboxAccordion
          productCount={productCount}
          accordionLabel={t('filter.menu.filter.brand')}
          {...brandRefinements}
        />
      );

    case FacetNames.MATERIAL:
      return (
        <FilterCheckboxAccordion
          productCount={productCount}
          accordionLabel={t('filter.menu.filter.material')}
          {...materialsRefinements}
        />
      );

    case FacetNames.PRICE:
      return <PriceFilter {...priceRange} />;

    default:
      return null;
  }
}
